/** "Coming up" rows for Enquiries-board deals whose tender return date is near or missed. Project tender returns
 *  already reach Home through buildProjectDeadlines; a deal that has not become a project never did. Pure: the
 *  caller decides who may read deals and passes them in. */

import type { WorkspaceDeal } from '../pipeline/types';
import { activeTenderDeadline } from '../pipeline/tenderDeadline';

export interface EnquiryTenderRow {
  id: string;
  title: string;
  context: string;
  due: Date;
  overdue: boolean;
  target: { view: 'workspace-pipeline' };
}

// A tender still unreturned a month after its date is a bid nobody is working on; the board is the place to
// close it, so Home stops nagging.
const LAPSED_DAYS = 30;

const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export function buildEnquiryTenderRows(
  deals: readonly Pick<WorkspaceDeal, 'id' | 'title' | 'client_name' | 'stage' | 'tender_deadline'>[],
  now: Date,
  windowDays: number,
): EnquiryTenderRow[] {
  const today = startOfDay(now);
  const rows: EnquiryTenderRow[] = [];
  for (const deal of deals) {
    const live = activeTenderDeadline(deal.stage, deal.tender_deadline, now);
    if (!live) continue;
    const due = startOfDay(live.date);
    // Round, not floor: a clock change makes a calendar day 23 or 25 hours long.
    const away = Math.round((due.getTime() - today.getTime()) / 86_400_000);
    if (away < -LAPSED_DAYS || away > windowDays) continue;
    const title =
      away < 0 ? `Tender overdue by ${days(-away)}` : away === 0 ? 'Tender due today' : `Tender due in ${days(away)}`;
    rows.push({
      id: `enquiry-tender-${deal.id}`,
      title,
      context: deal.client_name ? `${deal.title} · ${deal.client_name}` : deal.title,
      due,
      overdue: away < 0,
      target: { view: 'workspace-pipeline' },
    });
  }
  return rows;
}
